/**
 * "Log In As User" (web) — client side.
 *
 * Starting a session swaps the stored JWT for a token minted for the target
 * user and does a full reload, so initialState, access, menus and every
 * request rebuild as that user. The admin's own token is set aside in
 * LS_IMPERSONATOR_TOKEN and put back when the session ends (Return to my
 * account, countdown, Logout, or any 401 on the impersonation token).
 *
 * localStorage is shared by every tab, so other tabs reload whenever the
 * switch happens (watchImpersonationSwitch) — no tab may keep showing the
 * admin's UI while its requests already carry the target's token.
 */
import { LS_IMPERSONATOR_RETURN, LS_IMPERSONATOR_TOKEN, LS_PUSH_REBIND, LS_TOKEN_NAME, TENANT_ROLE_PARENTS } from "@/constants";
import { endLoginAs } from "@/services/app/tenant/users/impersonation";
import { disconnectTwilioStatus } from "@/services/app/tenant/twilio-connection";

type TokenClaims = {
  exp?: number;
  /** auth_token_id — unique per sign-in */
  sid?: string;
  impersonation?: boolean;
  impersonated_by?: number;
};

const read = (key: string): string => {
  try {
    return localStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
};

/** Decoded JWT payload, unverified — only for client-side UI decisions. */
export const getTokenClaims = (token?: string | null): TokenClaims | null => {
  const payload = (token || "").split(".")[1];
  if (!payload) return null;
  try {
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch (e) {
    return null;
  }
};

const hierarchyRole = (role: string): string | undefined => {
  const needle = role.trim().toLowerCase();
  return Object.keys(TENANT_ROLE_PARENTS).find((name) => name.toLowerCase() === needle);
};

const isRoleAtOrBelow = (role: string, ancestor: string): boolean => {
  let current = hierarchyRole(role) ?? null;
  const top = hierarchyRole(ancestor);
  if (!top) return false;
  while (current) {
    if (current === top) return true;
    current = TENANT_ROLE_PARENTS[current];
  }
  return false;
};

/**
 * Whether an admin with `adminRoles` may log in as someone with `targetRoles`
 * by the role hierarchy (super admins are handled by the caller). Hides the
 * Log In As action; the backend makes the real decision.
 */
export const rolesAllowLoginAs = (adminRoles: string[], targetRoles: string[]): boolean =>
  targetRoles.length > 0 &&
  targetRoles.every((targetRole) => adminRoles.some((adminRole) => isRoleAtOrBelow(targetRole, adminRole)));

/** True while this browser is logged in as another user. */
export const isImpersonating = (): boolean => getTokenClaims(read(LS_TOKEN_NAME))?.impersonation === true;

/** Epoch ms when the current session ends (JWT exp — independent of any timezone). */
export const impersonationEndsAt = (): number | null => {
  const exp = getTokenClaims(read(LS_TOKEN_NAME))?.exp;
  return exp ? exp * 1000 : null;
};

export const startImpersonation = (token: string) => {
  const ownToken = read(LS_TOKEN_NAME);
  localStorage.setItem(LS_IMPERSONATOR_RETURN, window.location.pathname + window.location.search);
  localStorage.setItem(LS_PUSH_REBIND, "1");
  localStorage.setItem(LS_TOKEN_NAME, token);
  // Set last: other tabs reload on this key, by then the new token is in place.
  localStorage.setItem(LS_IMPERSONATOR_TOKEN, ownToken);
  window.location.assign(PUBLIC_PATH);
};

let restoring = false;

/** Put the admin's own token back and reload where they started. False when there is nothing to restore. */
export const restoreImpersonator = (): boolean => {
  if (restoring) return true;
  const ownToken = read(LS_IMPERSONATOR_TOKEN);
  if (!ownToken) return false;

  restoring = true;
  const returnTo = read(LS_IMPERSONATOR_RETURN) || PUBLIC_PATH;
  localStorage.setItem(LS_PUSH_REBIND, "1");
  localStorage.setItem(LS_TOKEN_NAME, ownToken);
  localStorage.removeItem(LS_IMPERSONATOR_RETURN);
  localStorage.removeItem(LS_IMPERSONATOR_TOKEN);
  window.location.assign(returnTo);
  return true;
};

let ending = false;

/** Return to my account: log the user out as they would (phone offline too), then switch back. */
export const endImpersonation = async () => {
  // Once only — the banner can be on screen twice (layout + clock-in gate).
  if (ending) return;
  ending = true;
  try {
    await disconnectTwilioStatus({ reason: "logout" });
  } catch (e) {
    // Not on the phone — nothing to disconnect.
  }
  try {
    await endLoginAs();
  } catch (e) {
    // Already expired or revoked — switching back is all that's left.
  }
  if (!restoreImpersonator()) {
    localStorage.setItem(LS_TOKEN_NAME, "");
    window.location.assign(`${PUBLIC_PATH}user/login`);
  }
};

/**
 * For 401 handlers: an impersonation token that stopped working means the
 * session is over, so go back to the admin instead of the login page.
 * True when handled (a restore is now in progress).
 */
export const handleImpersonationAuthFailure = (): boolean => {
  if (restoring) return true;
  return isImpersonating() && restoreImpersonator();
};

/**
 * True once after a Log In As switch: the caller should re-register this
 * browser's push token, so notifications follow whoever is signed in now.
 */
export const consumePushRebind = (): boolean => {
  if (read(LS_PUSH_REBIND) !== "1") return false;
  localStorage.removeItem(LS_PUSH_REBIND);
  return true;
};

/** Forget the admin's set-aside token (e.g. after a fresh login). */
export const clearImpersonatorStash = () => {
  localStorage.removeItem(LS_IMPERSONATOR_RETURN);
  localStorage.removeItem(LS_IMPERSONATOR_TOKEN);
};

/**
 * At app boot: restore the admin if the session already ran out, or drop a
 * leftover admin token when this browser has since logged in normally.
 * True when the page is navigating away.
 */
export const restoreImpersonatorIfEnded = (): boolean => {
  if (!read(LS_IMPERSONATOR_TOKEN)) return false;

  const token = read(LS_TOKEN_NAME);
  const claims = getTokenClaims(token);
  if (!token || (claims?.impersonation && (!claims.exp || claims.exp * 1000 <= Date.now()))) {
    return restoreImpersonator();
  }
  if (!claims?.impersonation) {
    clearImpersonatorStash();
  }
  return false;
};

/** Reload this tab when another tab starts or ends a Log In As session. */
export const watchImpersonationSwitch = () => {
  window.addEventListener("storage", (e) => {
    if (e.key !== LS_IMPERSONATOR_TOKEN || restoring) return;
    if (e.newValue) {
      window.location.assign(PUBLIC_PATH);
    } else {
      window.location.reload();
    }
  });
};
